import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { OptionItem } from '@app/components/ui/select/select.type';
import {
  MonitoringFormTaskControllers,
  MonitoringFormTaskObjectType,
} from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';
import { MonitoringFormInfoType } from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';

import { useGetCommandsBulkQuery } from '@app/store/api/qm-module/qm-manage/qm-manage-commands.api';
import { useGetMonitoringFormsBulkQuery } from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { useGetUsersListWithRolesQuery } from '@app/store/api/qm-module/qm-users/qm-users.api';

import {
  useGetMFTaskControllersQuery,
  useGetMFTasksSingleQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';

import { Loading } from '@ui';

import NavigationTabs, {
  NavigationTabsItem,
} from '@app/components/ui/navigation-tabs/navigation-tabs';

import { navigationRoutes } from '@app/utils/navigation-routes';

import useTranslation from '@app/hooks/use-translation';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import MonitoringTaskSettingsHead from './monitoring-task-settings-head';

export type MonitoringTaskFormOutlet = {
  mFList: MonitoringFormInfoType[];
  mFTaskObject: MonitoringFormTaskObjectType | undefined;
  teamsAndOperatorsDataForFilter: Record<'qm_teams' | 'qm_operators', OptionItem[]>;
  isNew: boolean;
  thatTaskControllers: MonitoringFormTaskControllers | undefined;
};

const MonitoringTaskSettingsLayout = () => {
  const { t } = useTranslation('pages.qmManage.monitoring_tasks');

  const { id } = useParams();
  const isNew = id === 'new';
  // api
  const { data: userInfo } = useGetUserInfoQuery();
  const { data: thatTaskControllers, isLoading: controllersLoading } = useGetMFTaskControllersQuery(
    {
      id: id || '',
    },
    { skip: isNew || userInfo?.qm_role !== 'manager' },
  );

  const { data: mFTaskObject, isLoading: taskObjLoading } = useGetMFTasksSingleQuery(
    { id: id || '' },
    { skip: isNew },
  );

  const { data: monitoringFormsList, isLoading: monitoringFormsLoading } =
    useGetMonitoringFormsBulkQuery(
      {},
      {
        skip: userInfo?.qm_role !== 'manager',
      },
    );

  const { data: usersListWithRoles, isLoading: usersListWithRolesLoading } =
    useGetUsersListWithRolesQuery(
      {
        limit: 100,
        offset: 0,
        role_qm: 'operator',
      },
      {
        skip: userInfo?.qm_role !== 'manager',
      },
    );
  const { data: qmTeams, isLoading: qmTeamsLoading } = useGetCommandsBulkQuery(undefined, {
    skip: userInfo?.qm_role !== 'manager',
  });
  const isLoading =
    taskObjLoading ||
    monitoringFormsLoading ||
    usersListWithRolesLoading ||
    qmTeamsLoading ||
    controllersLoading;
  // api

  const teamsAndOperatorsDataForFilter: Record<'qm_teams' | 'qm_operators', OptionItem[]> = {
    qm_teams:
      qmTeams?.qm_teams
        ?.filter((t) => t.status === 'active')
        .map((team) => ({ title: team.name, value: team.team_id })) || [],
    qm_operators:
      usersListWithRoles?.qm_users?.map((operator) => ({
        title: operator.username,
        value: operator.id,
      })) || [],
  };
  const mFList: MonitoringFormInfoType[] = useMemo(() => {
    return Object.entries(monitoringFormsList || {})
      .map(([type, mfList]) => {
        return mfList?.reduce<MonitoringFormInfoType[]>((memo, item) => {
          return [
            ...memo,
            ...item.mforms
              .filter(({ status }) => status === 'ACTIVE')
              .map((i) => ({
                ...i,
                project: {
                  ...i.project,
                  project_id: item.project_id,
                  project_type: type as 'chats',
                },
              })),
          ];
        }, []);
      })
      ?.flat(Infinity) as MonitoringFormInfoType[];
  }, [monitoringFormsList]);

  const context: MonitoringTaskFormOutlet = {
    mFList,
    teamsAndOperatorsDataForFilter,
    mFTaskObject,
    isNew,
    thatTaskControllers,
  };
  const qmManageNavigationList: NavigationTabsItem[] = [
    {
      link: `.`,
      icon: 'FolderIcon',
      label: t('tabs.main'),
      index: 'qm_manage_monitoring_tasks_settings',
      hide: userInfo?.qm_role === 'controller',
    },
    {
      link: `./${navigationRoutes.qm_manage_monitoring_tasks_controllers}`,
      icon: 'FolderIcon',
      label: t('tabs.controllers'),
      index: 'qm_manage_monitoring_forms_tasks_controllers',
      hide: userInfo?.qm_role === 'controller',
    },
    {
      link: `./${navigationRoutes.qm_manage_monitoring_tasks_monitoring}`,
      icon: 'TableIcon',
      label: t('tabs.monitoring'),
      index: 'qm_manage_monitoring_forms_tasks_records',
    },
  ];

  if (isLoading) return <Loading />;

  return (
    <div>
      <MonitoringTaskSettingsHead isNew={isNew} mFTaskObject={mFTaskObject} />
      {!isNew && <NavigationTabs items={qmManageNavigationList} />}
      <Outlet context={context} />
    </div>
  );
};

export default MonitoringTaskSettingsLayout;
